// Push notification client helpers - handles service-worker registration,
// permission prompt, and subscribe / unsubscribe against the backend.

const API = process.env.REACT_APP_BACKEND_URL;

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

let registrationPromise = null;
export async function ensureServiceWorker() {
  if (!pushSupported()) return null;
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register('/sw.js');
  }
  return registrationPromise;
}

export async function getSubscription() {
  if (!pushSupported()) return null;
  const reg = await ensureServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
  if (!pushSupported()) {
    throw new Error('Push not supported on this device/browser.');
  }
  // Permission must be granted first.
  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') throw new Error('Permission denied.');
  } else if (Notification.permission === 'denied') {
    throw new Error('Notifications are blocked - enable them in browser settings.');
  }

  const keyRes = await fetch(`${API}/api/push/public-key`);
  const { public_key: publicKey } = await keyRes.json();
  if (!publicKey) throw new Error('Server VAPID key missing.');

  const reg = await ensureServiceWorker();
  if (!reg) throw new Error('Could not register service worker.');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await fetch(`${API}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sub.toJSON ? sub.toJSON() : sub),
  });
  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getSubscription();
  if (!sub) return;
  await fetch(`${API}/api/push/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  await sub.unsubscribe();
}
